import { FC, FormEvent } from 'react';

import { useForm, usePage } from '@inertiajs/react';
import { clsx } from 'clsx';

import { Routes } from '../../models/routes';
import { NovaPoshtaCitySelect } from '../nova_poshta/NovaPoshtaCitySelect';
import { Button } from '../ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../ui/Dialog';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';

type Props = {
    readonly open: boolean;
    readonly initialNickname?: string | null;
    readonly initialTelegramUsername?: string | null;
};

type FormValues = {
    user: {
        nickname: string;
        telegram_username: string;
        city: string;
        nova_poshta_city_ref: string;
    };
};

type PageProps = {
    errors?: Partial<
        Record<'email' | 'nickname' | 'telegram_username' | 'city', string>
    > | null;
};

export const OauthProfileCompletionDialog: FC<Props> = ({
    open,
    initialNickname,
    initialTelegramUsername,
}) => {
    const { errors: pageErrors } = usePage<PageProps>().props;
    const { data, setData, post, processing } = useForm<FormValues>({
        user: {
            nickname: initialNickname ?? '',
            telegram_username: initialTelegramUsername ?? '',
            city: '',
            nova_poshta_city_ref: '',
        },
    });
    const nicknameError = pageErrors?.nickname;
    const telegramUsernameError = pageErrors?.telegram_username;
    const cityError = pageErrors?.city;
    const emailError = pageErrors?.email;

    const submit = (e: FormEvent) => {
        e.preventDefault();
        post(Routes.CompleteGoogleOauthProfile);
    };

    return (
        <Dialog open={open}>
            <DialogContent
                hideCloseButton
                onEscapeKeyDown={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
                className="w-[calc(100%-2rem)] rounded-2xl border-border sm:max-w-md sm:rounded-2xl dark:border-white/10 dark:bg-[#111827] dark:shadow-2xl"
            >
                <DialogHeader>
                    <DialogTitle className="dark:text-slate-100">
                        Завершіть профіль
                    </DialogTitle>
                    <DialogDescription className="dark:text-slate-400">
                        Додайте нікнейм і Telegram, щоб завершити вхід через
                        Google. Місто можна вказати за бажанням.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={submit} className="space-y-4">
                    <div className="space-y-2">
                        <Label
                            htmlFor="oauth_nickname"
                            className="dark:text-slate-200"
                        >
                            Нікнейм
                        </Label>
                        <Input
                            id="oauth_nickname"
                            value={data.user.nickname}
                            onChange={(e) =>
                                setData('user', {
                                    ...data.user,
                                    nickname: e.target.value,
                                })
                            }
                            className={clsx(
                                'dark:border-white/10 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500',
                                {
                                    'border-red-600 dark:border-red-500':
                                        nicknameError,
                                },
                            )}
                            placeholder="Ваш нікнейм"
                            required
                        />
                        {nicknameError && (
                            <p className="text-sm text-red-600">
                                {nicknameError}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label
                            htmlFor="oauth_telegram_username"
                            className="dark:text-slate-200"
                        >
                            Telegram
                        </Label>
                        <Input
                            id="oauth_telegram_username"
                            value={data.user.telegram_username}
                            onChange={(e) =>
                                setData('user', {
                                    ...data.user,
                                    telegram_username: e.target.value,
                                })
                            }
                            className={clsx(
                                'dark:border-white/10 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500',
                                {
                                    'border-red-600 dark:border-red-500':
                                        telegramUsernameError,
                                },
                            )}
                            placeholder="@username"
                            required
                        />
                        {telegramUsernameError && (
                            <p className="text-sm text-red-600">
                                {telegramUsernameError}
                            </p>
                        )}
                    </div>

                    <NovaPoshtaCitySelect
                        id="oauth_city"
                        value={data.user.city}
                        required={false}
                        onSelect={(city) =>
                            setData('user', {
                                ...data.user,
                                city: city.name,
                                nova_poshta_city_ref: city.ref,
                            })
                        }
                        onFallbackInput={(city) =>
                            setData('user', {
                                ...data.user,
                                city,
                                nova_poshta_city_ref: '',
                            })
                        }
                        onSelectionClear={(city) =>
                            setData('user', {
                                ...data.user,
                                city,
                                nova_poshta_city_ref: '',
                            })
                        }
                        error={cityError}
                    />

                    {emailError && (
                        <p className="text-sm text-red-600">{emailError}</p>
                    )}

                    <Button
                        type="submit"
                        className="w-full"
                        disabled={processing}
                    >
                        {processing ? 'Збереження...' : 'Зберегти'}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
};
